<template>
  <div class="upload-works-page">
    <!-- 上传作品卡片 -->
    <div class="card">
      <h3 class="card-title">上传作品 <span class="required-tip">* 为必填项</span></h3>
      <div class="video-section">
        <div class="video-player" :class="{ 'has-video': form.videoFile }">
          <!-- 未上传视频时显示上传按钮 -->
          <div v-if="!form.videoFile" class="upload-placeholder">
            <input
              ref="videoInput"
              type="file"
              accept="video/*"
              style="display: none"
              @change="handleVideoChange"
            />
            <button class="btn-upload-video" @click="selectVideo">
              <span class="upload-icon">📹</span>
              <span>上传视频</span>
            </button>
            <p class="upload-hint">支持 MP4、MOV、AVI 等常见格式</p>
          </div>
          <!-- 已上传视频时显示预览 -->
          <div v-else class="video-preview">
            <video
              ref="videoPreview"
              :src="form.videoUrl"
              controls
              class="preview-player"
            ></video>
            <div class="video-info-bar">
              <span class="video-name">{{ form.videoFile.name }}</span>
              <span class="video-size">{{ formatFileSize(form.videoFile.size) }}</span>
              <button class="btn-remove-video" @click="removeVideo">移除</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 视频资料卡片 -->
    <div class="card">
      <h3 class="card-title">视频资料</h3>
      <div class="video-info-form">
        <div class="form-row">
          <div class="form-col form-col-large">
            <label>视频介绍 <span class="required">*</span></label>
            <input
              v-model="form.intro"
              type="text"
              placeholder="请输入视频介绍，让观众了解你的作品"
              :disabled="isSubmitting"
            />
          </div>
          <div class="form-col form-col-small">
            <label>视频分类 <span class="required">*</span></label>
            <div class="custom-select" :class="{ disabled: isSubmitting }" @click="!isSubmitting && (showDropdown = !showDropdown)">
              <span class="select-text" :class="{ placeholder: !form.category }">{{ form.category || '请选择' }}</span>
              <i class="el-icon-arrow-down"></i>
              <div v-if="showDropdown" class="dropdown-menu">
                <div
                  v-for="item in categoryList"
                  :key="item.value"
                  class="dropdown-item"
                  @click.stop="selectCategory(item.label)"
                >
                  {{ item.label }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 视频封面 -->
      <div class="cover-section">
        <h4 class="cover-title">
          视频封面 <span class="required">*</span>
          <span class="cover-tip">请根据视频比例选择图片上传</span>
        </h4>
        <div class="cover-upload-area">
          <!-- 竖版封面 -->
          <div class="cover-frame cover-portrait" :class="{ 'has-cover': form.coverPortrait }">
            <input
              ref="portraitInput"
              type="file"
              accept="image/*"
              style="display: none"
              @change="handlePortraitChange"
            />
            <div v-if="!form.coverPortrait" class="cover-placeholder">
              <button class="btn-cover-upload" @click="selectPortrait">上传图片</button>
              <span class="cover-ratio">竖版 9:16</span>
            </div>
            <div v-else class="cover-preview">
              <img :src="form.coverPortraitUrl" alt="竖版封面" />
              <div class="cover-actions">
                <button class="btn-edit-cover" @click="selectPortrait">更换</button>
                <button class="btn-remove-cover" @click="removePortrait">移除</button>
              </div>
            </div>
          </div>
          <!-- 横版封面 -->
          <div class="cover-frame cover-landscape" :class="{ 'has-cover': form.coverLandscape }">
            <input
              ref="landscapeInput"
              type="file"
              accept="image/*"
              style="display: none"
              @change="handleLandscapeChange"
            />
            <div v-if="!form.coverLandscape" class="cover-placeholder">
              <button class="btn-cover-upload" @click="selectLandscape">上传图片</button>
              <span class="cover-ratio">横版 16:9</span>
            </div>
            <div v-else class="cover-preview">
              <img :src="form.coverLandscapeUrl" alt="横版封面" />
              <div class="cover-actions">
                <button class="btn-edit-cover" @click="selectLandscape">更换</button>
                <button class="btn-remove-cover" @click="removeLandscape">移除</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 谁可以看 -->
      <div class="visibility-section">
        <span class="visibility-label">谁可以看</span>
        <label class="radio-item">
          <input v-model="form.visibility" type="radio" value="public" :disabled="isSubmitting" />
          <span class="radio-custom"></span>
          <span class="radio-text">所有人</span>
        </label>
        <label class="radio-item">
          <input v-model="form.visibility" type="radio" value="private" :disabled="isSubmitting" />
          <span class="radio-custom"></span>
          <span class="radio-text">私密</span>
        </label>
      </div>
    </div>

    <!-- 上传发布按钮 -->
    <div class="submit-section">
      <button
        class="btn-submit"
        :class="{ submitting: isSubmitting, submitted: isSubmitted }"
        :disabled="isSubmitting || isSubmitted"
        @click="submit"
      >
        <span v-if="isSubmitting" class="btn-loading">
          <span class="loading-spinner"></span>
          上传中...
        </span>
        <span v-else-if="isSubmitted">已提交审核</span>
        <span v-else>上传发布</span>
      </button>
    </div>

    <!-- 上传进度 -->
    <div v-if="isSubmitting || isSubmitted" class="progress-section">
      <div class="progress-card">
        <div class="progress-header">
          <span class="progress-title">上传进度</span>
          <span class="progress-status" :class="{ reviewing: isSubmitted }">{{ progressStatus }}</span>
        </div>
        <div class="progress-bar">
          <div class="progress-fill" :style="{ width: progressPercent + '%' }"></div>
        </div>
        <div class="progress-info">
          <span>{{ progressPercent }}%</span>
          <span v-if="isSubmitted" class="review-tip">审核预计1-3个工作日</span>
        </div>
      </div>
    </div>

    <!-- 已上传视频列表 -->
    <div v-if="uploadedVideos.length > 0" class="uploaded-list-section">
      <h3 class="section-title">我的投稿</h3>
      <div class="video-list">
        <div
          v-for="(video, index) in uploadedVideos"
          :key="video.id || index"
          class="video-list-item"
        >
          <div class="video-thumb">
            <img :src="video.coverUrl || video.coverPortraitUrl || video.coverLandscapeUrl" alt="封面" />
            <span class="video-duration">{{ video.duration || '--:--' }}</span>
          </div>
          <div class="video-meta">
            <h4 class="video-title">{{ video.title || video.intro || '未命名视频' }}</h4>
            <p class="video-desc">{{ video.category }} · {{ formatTime(video.uploadTime) }}</p>
            <div class="video-status">
              <span class="status-badge" :class="video.status">{{ getStatusText(video.status) }}</span>
            </div>
          </div>
          <div class="video-actions">
            <button class="btn-action-remove" @click="removeUploadedVideo(index)">移除</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'UploadWorksPage',
  data() {
    return {
      showDropdown: false,
      isSubmitting: false,
      isSubmitted: false,
      progressPercent: 0,
      progressStatus: '准备上传',
      form: {
        intro: '',
        category: '',
        visibility: 'public',
        videoFile: null,
        videoUrl: '',
        coverPortrait: null,
        coverPortraitUrl: '',
        coverLandscape: null,
        coverLandscapeUrl: ''
      },
      categoryList: [
        { label: '音乐', value: 'music' },
        { label: '舞蹈', value: 'dance' },
        { label: '乐器', value: 'instrument' },
        { label: '脱口秀', value: 'talkshow' },
        { label: '二次元', value: 'anime' },
        { label: '颜值', value: 'beauty' },
        { label: '才艺', value: 'talent' },
        { label: '生活方式', value: 'lifestyle' }
      ],
      uploadedVideos: []
    }
  },
  methods: {
    // 选择视频文件
    selectVideo() {
      this.$refs.videoInput.click()
    },
    // 处理视频文件选择
    handleVideoChange(e) {
      const file = e.target.files[0]
      if (!file) return

      // 验证文件类型
      if (!file.type.startsWith('video/')) {
        this.$message.error('请选择视频文件')
        return
      }

      // 验证文件大小（最大2GB）
      const maxSize = 2 * 1024 * 1024 * 1024
      if (file.size > maxSize) {
        this.$message.error('视频文件不能超过2GB')
        return
      }

      this.form.videoFile = file
      this.form.videoUrl = URL.createObjectURL(file)
      this.$message.success('视频已选择：' + file.name)
    },
    // 移除视频
    removeVideo() {
      this.form.videoFile = null
      this.form.videoUrl = ''
      if (this.$refs.videoInput) {
        this.$refs.videoInput.value = ''
      }
    },
    // 选择竖版封面
    selectPortrait() {
      this.$refs.portraitInput.click()
    },
    // 处理竖版封面选择
    handlePortraitChange(e) {
      const file = e.target.files[0]
      if (!file) return

      if (!file.type.startsWith('image/')) {
        this.$message.error('请选择图片文件')
        return
      }

      this.form.coverPortrait = file
      this.form.coverPortraitUrl = URL.createObjectURL(file)
    },
    // 移除竖版封面
    removePortrait() {
      this.form.coverPortrait = null
      this.form.coverPortraitUrl = ''
      if (this.$refs.portraitInput) {
        this.$refs.portraitInput.value = ''
      }
    },
    // 选择横版封面
    selectLandscape() {
      this.$refs.landscapeInput.click()
    },
    // 处理横版封面选择
    handleLandscapeChange(e) {
      const file = e.target.files[0]
      if (!file) return

      if (!file.type.startsWith('image/')) {
        this.$message.error('请选择图片文件')
        return
      }

      this.form.coverLandscape = file
      this.form.coverLandscapeUrl = URL.createObjectURL(file)
    },
    // 移除横版封面
    removeLandscape() {
      this.form.coverLandscape = null
      this.form.coverLandscapeUrl = ''
      if (this.$refs.landscapeInput) {
        this.$refs.landscapeInput.value = ''
      }
    },
    // 格式化文件大小
    formatFileSize(bytes) {
      if (bytes === 0) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },
    // 格式化时间
    formatTime(time) {
      if (!time) return ''
      const date = new Date(time)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    // 获取状态文本
    getStatusText(status) {
      const statusMap = {
        'reviewing': '审核中',
        'approved': '已通过',
        'rejected': '未通过'
      }
      return statusMap[status] || status
    },
    selectCategory(label) {
      this.form.category = label
      this.showDropdown = false
    },
    // 提交发布
    submit() {
      // 表单验证
      if (!this.form.videoFile) {
        this.$message.error('请上传视频文件')
        return
      }
      if (!this.form.intro.trim()) {
        this.$message.error('请输入视频介绍')
        return
      }
      if (!this.form.category) {
        this.$message.error('请选择视频分类')
        return
      }
      if (!this.form.coverPortrait && !this.form.coverLandscape) {
        this.$message.error('请至少上传一张封面图片')
        return
      }

      // 开始上传流程
      this.isSubmitting = true
      this.progressPercent = 0
      this.progressStatus = '正在上传...'

      // 模拟上传进度
      this.simulateUpload()
    },
    // 模拟上传进度
    simulateUpload() {
      const interval = setInterval(() => {
        if (this.progressPercent < 90) {
          this.progressPercent += Math.floor(Math.random() * 10) + 5
          if (this.progressPercent > 90) {
            this.progressPercent = 90
          }
        } else {
          clearInterval(interval)
          // 上传完成，进入审核状态
          setTimeout(() => {
            this.progressPercent = 100
            this.progressStatus = '审核中'
            this.isSubmitting = false
            this.isSubmitted = true
            this.$message.success('视频提交成功！审核预计1-3个工作日')

            // 保存到已上传列表
            this.saveToUploadedList()
          }, 500)
        }
      }, 300)
    },
    // 保存到已上传列表
    saveToUploadedList() {
      const videoInfo = {
        id: Date.now(),
        intro: this.form.intro,
        category: this.form.category,
        visibility: this.form.visibility,
        coverPortraitUrl: this.form.coverPortraitUrl,
        coverLandscapeUrl: this.form.coverLandscapeUrl,
        coverUrl: this.form.coverPortraitUrl || this.form.coverLandscapeUrl,
        uploadTime: new Date().toISOString(),
        status: 'reviewing',
        duration: '--:--'
      }
      this.uploadedVideos.unshift(videoInfo)

      // 清空表单
      this.resetForm()
    },
    // 重置表单
    resetForm() {
      this.form = {
        intro: '',
        category: '',
        visibility: 'public',
        videoFile: null,
        videoUrl: '',
        coverPortrait: null,
        coverPortraitUrl: '',
        coverLandscape: null,
        coverLandscapeUrl: ''
      }
      // 安全地重置文件输入框
      if (this.$refs.videoInput) {
        this.$refs.videoInput.value = ''
      }
      if (this.$refs.portraitInput) {
        this.$refs.portraitInput.value = ''
      }
      if (this.$refs.landscapeInput) {
        this.$refs.landscapeInput.value = ''
      }
    },
    // 移除已上传的视频
    removeUploadedVideo(index) {
      this.$confirm('确定要移除这个视频吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.uploadedVideos.splice(index, 1)
        this.$message.success('已移除')
      }).catch(() => {})
    }
  }
}
</script>

<style scoped lang="scss">
.upload-works-page {
  min-height: 100vh;
  background: #f5f5f5;
  padding: 20px;
}

.card {
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 16px;
}

.card-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 0 0 20px 0;
}

// 视频区域
.video-section {
  .video-player {
    width: 100%;
    aspect-ratio: 16 / 9;
    background: #000;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 16px;

    .btn-upload-video {
      padding: 10px 32px;
      background: #4a4a4a;
      border: none;
      border-radius: 20px;
      color: #fff;
      font-size: 14px;
      cursor: pointer;

      &:hover {
        background: #555;
      }
    }
  }

  .video-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .btn-copy {
      padding: 8px 24px;
      background: #fff;
      border: 1px solid #e0e0e0;
      border-radius: 20px;
      color: #666;
      font-size: 13px;
      cursor: pointer;

      &:hover {
        background: #f8f8f8;
      }
    }

    .btn-start {
      padding: 10px 32px;
      background: #8B5CF6;
      border: none;
      border-radius: 24px;
      color: #fff;
      font-size: 14px;
      cursor: pointer;

      &:hover {
        opacity: 0.9;
      }
    }
  }
}

// 视频资料表单
.video-info-form {
  margin-bottom: 24px;

  .form-row {
    display: flex;
    gap: 16px;
  }

  .form-col {
    &.form-col-large {
      flex: 3;
    }

    &.form-col-small {
      flex: 1;
    }

    label {
      display: block;
      font-size: 13px;
      color: #333;
      margin-bottom: 8px;
    }

    input {
      width: 100%;
      padding: 12px 16px;
      border: 1px solid #e8e8e8;
      border-radius: 24px;
      font-size: 14px;
      color: #333;
      background: #fafafa;
      outline: none;
      box-sizing: border-box;

      &:focus {
        border-color: #8B5CF6;
      }
    }

    .custom-select {
      position: relative;
      width: 100%;
      padding: 12px 16px;
      border: 1px solid #e8e8e8;
      border-radius: 24px;
      font-size: 14px;
      color: #333;
      background: #fafafa;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: space-between;
      box-sizing: border-box;

      .select-text {
        color: #999;
      }

      .el-icon-arrow-down {
        color: #999;
        font-size: 12px;
      }

      .dropdown-menu {
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        margin-top: 4px;
        background: #fff;
        border: 1px solid #e8e8e8;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        z-index: 100;
        max-height: 200px;
        overflow-y: auto;

        .dropdown-item {
          padding: 10px 16px;
          font-size: 14px;
          color: #333;
          cursor: pointer;

          &:hover {
            background: #f5f5f5;
          }
        }
      }
    }
  }
}

// 封面区域
.cover-section {
  margin-bottom: 24px;

  .cover-title {
    font-size: 15px;
    font-weight: 600;
    color: #333;
    margin: 0 0 16px 0;

    .cover-tip {
      font-size: 12px;
      color: #999;
      font-weight: normal;
      margin-left: 8px;
    }
  }

  .cover-upload-area {
    display: flex;
    gap: 40px;
    justify-content: center;

    .cover-frame {
      border: 1px solid #e0e0e0;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #f5f5ff;

      &.cover-portrait {
        width: 180px;
        height: 280px;
      }

      &.cover-landscape {
        width: 320px;
        height: 180px;
      }

      .btn-cover-upload {
        padding: 8px 32px;
        background: #b0b0b0;
        border: none;
        border-radius: 16px;
        color: #fff;
        font-size: 13px;
        cursor: pointer;

        &:hover {
          background: #999;
        }
      }
    }
  }
}

// 谁可以看
.visibility-section {
  display: flex;
  align-items: center;
  gap: 32px;

  .visibility-label {
    font-size: 15px;
    font-weight: 600;
    color: #333;
  }

  .radio-item {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;

    input[type="radio"] {
      display: none;
    }

    .radio-custom {
      width: 16px;
      height: 16px;
      border: 2px solid #8B5CF6;
      border-radius: 4px;
      display: flex;
      align-items: center;
      justify-content: center;

      &::after {
        content: '';
        width: 8px;
        height: 8px;
        background: #8B5CF6;
        border-radius: 2px;
        transform: scale(0);
        transition: transform 0.2s ease;
      }
    }

    input[type="radio"]:checked + .radio-custom::after {
      transform: scale(1);
    }

    .radio-text {
      font-size: 14px;
      color: #333;
    }
  }
}

// 必填项标记
.required {
  color: #ff4d4f;
  margin-left: 4px;
}

.required-tip {
  font-size: 12px;
  color: #999;
  font-weight: normal;
  margin-left: 8px;
}

// 视频区域增强
.video-section {
  .video-player {
    width: 100%;
    aspect-ratio: 16 / 9;
    background: #1a1a1a;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 16px;
    overflow: hidden;

    &.has-video {
      background: #000;
    }

    .upload-placeholder {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;

      .btn-upload-video {
        padding: 14px 40px;
        background: linear-gradient(135deg, #8B5CF6, #7c3aed);
        border: none;
        border-radius: 24px;
        color: #fff;
        font-size: 15px;
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 8px;
        transition: all 0.3s;

        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(139, 92, 246, 0.4);
        }

        .upload-icon {
          font-size: 18px;
        }
      }

      .upload-hint {
        font-size: 12px;
        color: #999;
      }
    }

    .video-preview {
      width: 100%;
      height: 100%;
      display: flex;
      flex-direction: column;

      .preview-player {
        flex: 1;
        width: 100%;
        height: 100%;
        object-fit: contain;
        display: block;
        margin: 0 auto;
      }

      .video-info-bar {
        display: flex;
        align-items: center;
        gap: 16px;
        padding: 10px 16px;
        background: rgba(0, 0, 0, 0.8);
        color: #fff;
        font-size: 13px;

        .video-name {
          flex: 1;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .video-size {
          color: #aaa;
        }

        .btn-remove-video {
          padding: 4px 12px;
          background: transparent;
          border: 1px solid #ff4d4f;
          border-radius: 4px;
          color: #ff4d4f;
          font-size: 12px;
          cursor: pointer;

          &:hover {
            background: #ff4d4f;
            color: #fff;
          }
        }
      }
    }
  }
}

// 封面区域增强
.cover-section {
  margin-bottom: 24px;

  .cover-title {
    font-size: 15px;
    font-weight: 600;
    color: #333;
    margin: 0 0 16px 0;

    .cover-tip {
      font-size: 12px;
      color: #999;
      font-weight: normal;
      margin-left: 8px;
    }
  }

  .cover-upload-area {
    display: flex;
    gap: 40px;
    justify-content: center;

    .cover-frame {
      border: 2px dashed #e0e0e0;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #fafafa;
      transition: all 0.3s;
      overflow: hidden;
      position: relative;

      &:hover {
        border-color: #8B5CF6;
        background: #f5f5ff;
      }

      &.has-cover {
        border-style: solid;
        border-color: #e0e0e0;
      }

      &.cover-portrait {
        width: 180px;
        height: 280px;
      }

      &.cover-landscape {
        width: 320px;
        height: 180px;
      }

      .cover-placeholder {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;

        .btn-cover-upload {
          padding: 8px 24px;
          background: #8B5CF6;
          border: none;
          border-radius: 16px;
          color: #fff;
          font-size: 13px;
          cursor: pointer;

          &:hover {
            background: #7c3aed;
          }
        }

        .cover-ratio {
          font-size: 11px;
          color: #999;
        }
      }

      .cover-preview {
        width: 100%;
        height: 100%;
        position: relative;

        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .cover-actions {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          display: flex;
          gap: 8px;
          padding: 8px;
          background: rgba(0, 0, 0, 0.6);

          button {
            flex: 1;
            padding: 6px 0;
            border: none;
            border-radius: 4px;
            font-size: 12px;
            cursor: pointer;
          }

          .btn-edit-cover {
            background: #8B5CF6;
            color: #fff;
          }

          .btn-remove-cover {
            background: #ff4d4f;
            color: #fff;
          }
        }
      }
    }
  }
}

// 表单禁用状态
.custom-select.disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

// 提交按钮
.submit-section {
  display: flex;
  justify-content: center;
  padding: 8px 0;

  .btn-submit {
    padding: 14px 56px;
    background: linear-gradient(135deg, #8B5CF6, #7c3aed);
    border: none;
    border-radius: 28px;
    color: #fff;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s;
    min-width: 160px;

    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(139, 92, 246, 0.4);
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.8;
    }

    &.submitting {
      background: linear-gradient(135deg, #a78bfa, #8B5CF6);
    }

    &.submitted {
      background: linear-gradient(135deg, #52c41a, #389e0d);
    }

    .btn-loading {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;

      .loading-spinner {
        width: 16px;
        height: 16px;
        border: 2px solid rgba(255, 255, 255, 0.3);
        border-top-color: #fff;
        border-radius: 50%;
        animation: spin 0.8s linear infinite;
      }
    }
  }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

// 进度区域
.progress-section {
  margin-top: 24px;

  .progress-card {
    background: #fff;
    border-radius: 12px;
    padding: 20px 24px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);

    .progress-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 12px;

      .progress-title {
        font-size: 14px;
        font-weight: 600;
        color: #333;
      }

      .progress-status {
        font-size: 13px;
        color: #8B5CF6;
        font-weight: 500;

        &.reviewing {
          color: #faad14;
        }
      }
    }

    .progress-bar {
      height: 8px;
      background: #f0f0f0;
      border-radius: 4px;
      overflow: hidden;
      margin-bottom: 10px;

      .progress-fill {
        height: 100%;
        background: linear-gradient(90deg, #8B5CF6, #a78bfa);
        border-radius: 4px;
        transition: width 0.3s ease;
      }
    }

    .progress-info {
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 13px;
      color: #666;

      .review-tip {
        color: #faad14;
        font-size: 12px;
      }
    }
  }
}

// 已上传列表
.uploaded-list-section {
  margin-top: 32px;

  .section-title {
    font-size: 16px;
    font-weight: 600;
    color: #333;
    margin-bottom: 16px;
    padding-left: 8px;
    border-left: 4px solid #8B5CF6;
  }

  .video-list {
    display: flex;
    flex-direction: column;
    gap: 16px;

    .video-list-item {
      display: flex;
      align-items: center;
      gap: 16px;
      background: #fff;
      border-radius: 12px;
      padding: 16px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

      .video-thumb {
        width: 160px;
        height: 90px;
        border-radius: 8px;
        overflow: hidden;
        position: relative;
        flex-shrink: 0;

        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .video-duration {
          position: absolute;
          bottom: 4px;
          right: 4px;
          padding: 2px 6px;
          background: rgba(0, 0, 0, 0.7);
          color: #fff;
          font-size: 11px;
          border-radius: 4px;
        }
      }

      .video-meta {
        flex: 1;
        min-width: 0;

        .video-title {
          font-size: 15px;
          font-weight: 600;
          color: #333;
          margin: 0 0 6px 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .video-desc {
          font-size: 13px;
          color: #999;
          margin: 0 0 10px 0;
        }

        .video-status {
          .status-badge {
            display: inline-block;
            padding: 4px 10px;
            border-radius: 4px;
            font-size: 12px;
            font-weight: 500;

            &.reviewing {
              background: #fff7e6;
              color: #faad14;
            }

            &.approved {
              background: #f6ffed;
              color: #52c41a;
            }

            &.rejected {
              background: #fff2f0;
              color: #ff4d4f;
            }
          }
        }
      }

      .video-actions {
        .btn-action-remove {
          padding: 8px 20px;
          background: transparent;
          border: 1px solid #ff4d4f;
          border-radius: 6px;
          color: #ff4d4f;
          font-size: 13px;
          cursor: pointer;
          transition: all 0.2s;

          &:hover {
            background: #ff4d4f;
            color: #fff;
          }
        }
      }
    }
  }
}
</style>